import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useRef, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, UploadCloud } from "lucide-react";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { cn } from "../../../lib/utils";
import {
  submitAnnouncement,
  publishAnnouncement,
  resubmitAnnouncement,
  updateAnnouncementDraft,
  fetchDepartments,
  fetchManagers,
  fileToBase64,
  saveAnnouncementDocument } from
"../../../services/announcementServices";
import { AUDIENCE_OPTIONS, MAX_FILE_BYTES, ALLOWED_EXTENSIONS, IMPORTANT_COLOR, GENERAL_COLOR } from "../constants/announcementOptions";

const fieldLabel = "block text-xs font-semibold per-file-hint mb-1.5";
const inputBase =
"w-full h-10 px-3 rounded-xl border bg-white text-sm outline-hidden focus:ring-1 focus:ring-accent";
const okBorder = "border-(--color-text-border)";
const errBorder = "border-error";

function getTodayISO() {
  return new Date().toISOString().split("T")[0];
}

export function AddAnnouncementModal({ open, onClose, initialData, isHR = false }) {
  const isEdit = Boolean(initialData);
  const isDraftResume = (initialData?.status ?? "").toLowerCase() === "draft";
  const hrUser = isHR;
  const todayISO = getTodayISO();

  const savedPublishISO = initialData?.publishDateISO || "";
  const initialPublishDate = savedPublishISO && savedPublishISO >= todayISO ? savedPublishISO : todayISO;

  const [title, setTitle] = useState(initialData?.title ?? "");
  const [archiveDate, setArchiveDate] = useState(initialData?.archiveDateISO ?? "");
  const [publishDate, setPublishDate] = useState(initialPublishDate);
  const [publishError, setPublishError] = useState("");
  const [category, setCategory] = useState(initialData?.priority ?? initialData?.type ?? "Important");
  const [content, setContent] = useState(initialData?.content ?? "");
  const [recipient, setRecipient] = useState(initialData?.audience ?? "");
  const [departmentId, setDepartmentId] = useState(initialData?.departmentId ?? 0);
  const [departments, setDepartments] = useState([]);
  const [managerId, setManagerId] = useState(initialData?.managerId ?? 0);
  const [managers, setManagers] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [existingDocRemoved, setExistingDocRemoved] = useState(false);
  const [fileError, setFileError] = useState("");
  const [docDownloading, setDocDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    title: false, recipient: false, content: false,
    archiveDate: false, publishDate: false, departmentId: false, managerId: false
  });
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);

  // Fetch departments only when "Department" is selected.
  useEffect(() => {
    if (recipient !== "Department") return;
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]));
  }, [recipient]);

  // Fetch managers only when "Reportees" is selected.
  useEffect(() => {
    if (recipient !== "Reportees") return;
    fetchManagers().then(setManagers).catch(() => setManagers([]));
  }, [recipient]);

  const clearFieldError = (field) => setFieldErrors((prev) => ({ ...prev, [field]: false }));

  const acceptFile = (file) => {
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError("Only PDF, JPG, JPEG, DOC or DOCX files are allowed.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("File is larger than 5MB. Please choose a smaller file.");
      return;
    }
    setFileError("");
    setAttachedFiles([file]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    acceptFile(file);
  };
  const handleFileDrop = (e) => {
    e.preventDefault();
    acceptFile(e.dataTransfer?.files?.[0]);
  };
  const removeFile = () => {
    setAttachedFiles([]);
    setFileError("");
  };

  const hasExistingDoc = isEdit && Boolean(initialData?.hasSupportingDocument) && !existingDocRemoved;
  const hasAnyDoc = hasExistingDoc || attachedFiles.length > 0;

  const handleViewExistingDoc = async () => {
    if (!initialData?.announcementId || docDownloading) return;
    setDocDownloading(true);
    setFileError("");
    try {
      await saveAnnouncementDocument(initialData.announcementId);
    } catch (err) {
      setFileError(`Download failed: ${err.message}`);
    } finally {
      setDocDownloading(false);
    }
  };

  const hasChanges = !isEdit || isDraftResume ||
  title.trim() !== (initialData?.title ?? "").trim() ||
  content.trim() !== (initialData?.content ?? "").trim() ||
  category !== (initialData?.priority ?? initialData?.type ?? "Important") ||
  recipient !== (initialData?.audience ?? "") ||
  departmentId !== (initialData?.departmentId ?? 0) ||
  managerId !== (initialData?.managerId ?? 0) ||
  archiveDate !== (initialData?.archiveDateISO ?? "") ||
  publishDate !== initialPublishDate ||
  attachedFiles.length > 0 ||
  existingDocRemoved;


  const handleSubmit = async () => {
    if (submittingRef.current) return;

    const requireFullValidation = !isEdit || isDraftResume;
    const errors = {
      title: !title.trim(),
      recipient: !recipient,
      content: !content.trim(),
      archiveDate: requireFullValidation && !archiveDate,
      departmentId: requireFullValidation && recipient === "Department" && !departmentId,
      managerId: requireFullValidation && recipient === "Reportees" && !managerId
    };
    setFieldErrors(errors);
    if (errors.title || errors.recipient || errors.content || errors.archiveDate ||
    errors.departmentId || errors.managerId ||
    (requireFullValidation && archiveError) || publishError) {
      setSubmitError("Please fill in all required fields marked with *");
      return;
    }
    if (isEdit && !hasChanges) {
      setSubmitError("Make a change before resubmitting — HR needs to see what was corrected.");
      return;
    }

    submittingRef.current = true;
    setSubmitError("");
    try {
      setSubmitting(true);

      if (isEdit && initialData?.announcementId) {
        let supportingDocumentBase64;
        if (attachedFiles.length > 0) {
          try {supportingDocumentBase64 = await fileToBase64(attachedFiles[0]);} catch {/* ignore */}
        } else if (existingDocRemoved) {
          supportingDocumentBase64 = "";
        }

        if (isDraftResume && hrUser) {
          await publishAnnouncement(initialData.announcementId, "", {
            announcementTitle: title.trim(),
            announcementBody: content.trim(),
            announcementType: category,
            recipients: recipient,
            departmentId,
            managerId,
            archiveDate: archiveDate || initialData?.archiveDateISO || "",
            publishDate: publishDate || initialData?.publishDateISO || todayISO,
            supportingDocumentBase64:
            supportingDocumentBase64 ?? initialData?.supportingDocumentBase64 ?? ""
          });
        } else {
          await resubmitAnnouncement(initialData.announcementId, {
            announcementTitle: title.trim(),
            announcementBody: content.trim(),
            announcementType: category,
            recipients: recipient,
            departmentId,
            managerId,
            archiveDate: archiveDate || initialData?.archiveDateISO || "",
            publishDate: publishDate || initialData?.publishDateISO || todayISO,
            remarks: "",
            ...(supportingDocumentBase64 !== undefined ? { supportingDocumentBase64 } : {})
          });
        }
      } else {
        const { announcementId } = await submitAnnouncement({
          title, content, category, recipient, departmentId, managerId,
          attachedFiles, archiveDate, publishDate,
          announcementStatus: "Pending"
        });
        if (hrUser && announcementId) {
          await publishAnnouncement(announcementId, "", {
            announcementTitle: title.trim(),
            announcementBody: content.trim(),
            announcementType: category,
            recipients: recipient,
            departmentId,
            managerId,
            archiveDate,
            publishDate
          });
        }
      }

      const defaultMsg = isDraftResume ?
      hrUser ? "Announcement published successfully!" : "Announcement submitted to HR for review!" :
      isEdit ?
      "Announcement resubmitted to HR for review!" :
      hrUser ?
      "Announcement published successfully!" :
      "Announcement sent to HR for review!";
      onClose(true, defaultMsg);
    } catch (err) {
      setSubmitError(err.status === 401 ?
      "Session expired. Please refresh the page and log in again." :
      `Submission failed: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (submittingRef.current) return;
    if (!title.trim()) {
      setFieldErrors((prev) => ({ ...prev, title: true }));
      setSubmitError("Please enter a title before saving as a draft.");
      return;
    }
    submittingRef.current = true;
    setSubmitError("");
    try {
      setSavingDraft(true);

      if (isEdit && initialData?.announcementId) {
        let supportingDocumentBase64;
        if (attachedFiles.length > 0) {
          try {supportingDocumentBase64 = await fileToBase64(attachedFiles[0]);} catch {/* ignore */}
        } else if (existingDocRemoved) {
          supportingDocumentBase64 = "";
        }

        await updateAnnouncementDraft(initialData.announcementId, {
          announcementTitle: title.trim(),
          announcementBody: content.trim(),
          announcementType: category,
          recipients: recipient,
          departmentId,
          managerId,
          archiveDate: archiveDate || initialData?.archiveDateISO || "",
          publishDate: publishDate || initialData?.publishDateISO || todayISO,
          ...(supportingDocumentBase64 !== undefined ? { supportingDocumentBase64 } : {})
        });
      } else {
        await submitAnnouncement({
          title, content, category, recipient, departmentId, managerId,
          attachedFiles, archiveDate, publishDate,
          announcementStatus: "Draft"
        });
      }

      onClose(true, "Saved as draft.");
    } catch (err) {
      setSubmitError(err.status === 401 ?
      "Session expired. Please refresh the page and log in again." :
      `Failed to save draft: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setSavingDraft(false);
    }
  };

  const submitLabel =
  isEdit && !isDraftResume ?
  submitting ? "Resubmitting…" : "Resubmit to HR" :
  hrUser ?
  publishDate > todayISO ?
  submitting ? "Scheduling…" : "Schedule" :
  submitting ? "Publishing…" : "Publish" :
  submitting ? "Sending…" : "Send to HR";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose(false)}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[95vw] sm:w-[560px] max-w-[560px] max-h-[92vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 font-sans",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}>
          
          <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70 shrink-0">
            <DialogTitle className="text-[18px] font-bold text-foreground">
              {isDraftResume ? "Complete Draft" : isEdit ? "Edit Announcement" : "Add Announcement"}
            </DialogTitle>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-workspace"
              aria-label={i18n.t("formActions.close")}>
              
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Title */}
            <div>
              <label className={fieldLabel}><Trans i18nKey="common.title" /> <span className="text-error">*</span></label>
              <input
                type="text"
                placeholder={i18n.t("announcements.writeHere")}
                value={title}
                onChange={(e) => {setTitle(e.target.value);clearFieldError("title");setSubmitError("");}}
                className={cn(inputBase, fieldErrors.title ? errBorder : okBorder)} />
              
              {fieldErrors.title && <p className="text-[11px] text-error mt-1"><Trans i18nKey="validation.titleIsRequired" /></p>}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={fieldLabel}><Trans i18nKey="dateTime.publishDate" /> <span className="text-error">*</span></label>
                <input
                  type="date"
                  min={todayISO}
                  max="2099-12-31"
                  value={publishDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {setPublishError("");clearFieldError("publishDate");setSubmitError("");return;}
                    const year = parseInt(val.split("-")[0], 10);
                    if (val < todayISO) {
                      setPublishError("Publish date cannot be before today.");
                    } else if (year > 2099) {
                      setPublishError("Please enter a valid year (up to 2099).");
                      setPublishDate(todayISO);clearFieldError("publishDate");setSubmitError("");return;
                    } else {
                      setPublishError("");
                    }
                    setPublishDate(val);clearFieldError("publishDate");setSubmitError("");
                  }}
                  onBlur={(e) => {if (!e.target.value) setPublishDate(todayISO);}}
                  className={cn(inputBase, publishError || fieldErrors.publishDate ? errBorder : okBorder)} />
                
                {publishError && <p className="text-[11px] text-error mt-1">{publishError}</p>}
                {!publishError && fieldErrors.publishDate &&
                <p className="text-[11px] text-error mt-1"><Trans i18nKey="validation.publishDateIsRequired" /></p>
                }
              </div>
              <div>
                <label className={fieldLabel}><Trans i18nKey="dateTime.archiveDate" /> <span className="text-error">*</span></label>
                <input
                  type="date"
                  min={todayISO}
                  max="2099-12-31"
                  value={archiveDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {setArchiveError("");clearFieldError("archiveDate");setSubmitError("");return;}
                    const year = parseInt(val.split("-")[0], 10);
                    if (val < todayISO) {
                      setArchiveError("Archive Date cannot be before today.");
                    } else if (year > 2099) {
                      setArchiveError("Please enter a valid year (up to 2099).");
                      setArchiveDate("");clearFieldError("archiveDate");setSubmitError("");return;
                    } else {
                      setArchiveError("");
                    }
                    setArchiveDate(val);clearFieldError("archiveDate");setSubmitError("");
                  }}
                  onBlur={(e) => {if (!e.target.value) setArchiveDate("");}}
                  className={cn(inputBase, archiveError || fieldErrors.archiveDate ? errBorder : okBorder)} />
                
                {archiveError && <p className="text-[11px] text-error mt-1">{archiveError}</p>}
                {!archiveError && fieldErrors.archiveDate &&
                <p className="text-[11px] text-error mt-1"><Trans i18nKey="validation.archiveDateIsRequired" /></p>
                }
              </div>
            </div>

            {/* Category */}
            <div>
              <label className={fieldLabel}><Trans i18nKey="common.category" /> <span className="text-error">*</span></label>
              <div className="flex gap-6 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="category" checked={category === "Important"} onChange={() => setCategory("Important")} />
                  <span className="font-semibold" style={{ color: IMPORTANT_COLOR }}><Trans i18nKey="formActions.important" /></span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="category" checked={category === "General"} onChange={() => setCategory("General")} />
                  <span className="font-semibold" style={{ color: GENERAL_COLOR }}><Trans i18nKey="common.general" /></span>
                </label>
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label className={fieldLabel}><Trans i18nKey="common.recipients" /> <span className="text-error">*</span></label>
              <Select
                value={recipient || undefined}
                onValueChange={(val) => {
                  setRecipient(val);setDepartmentId(0);setManagerId(0);
                  clearFieldError("recipient");clearFieldError("departmentId");clearFieldError("managerId");setSubmitError("");
                }}>
                
                <SelectTrigger className={cn("h-10 rounded-xl bg-white", fieldErrors.recipient ? errBorder : "border-(--color-text-border)")}>
                  <SelectValue placeholder={i18n.t("announcements.selectAudience")} />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((option) =>
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {fieldErrors.recipient && <p className="text-[11px] text-error mt-1"><Trans i18nKey="announcements.pleaseSelectARecipient" /></p>}
            </div>

            {/* Department */}
            {recipient === "Department" &&
            <div>
                <label className={fieldLabel}><Trans i18nKey="common.department" /> <span className="text-error">*</span></label>
                <Select
                value={departmentId ? String(departmentId) : undefined}
                onValueChange={(val) => {setDepartmentId(Number(val));clearFieldError("departmentId");setSubmitError("");}}>
                
                  <SelectTrigger className={cn("h-10 rounded-xl bg-white", fieldErrors.departmentId ? errBorder : "border-(--color-text-border)")}>
                    <SelectValue placeholder={i18n.t("announcements.selectDepartment")} />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) =>
                  <SelectItem key={d.departmentId} value={String(d.departmentId)}>{d.departmentName}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
                {fieldErrors.departmentId && <p className="text-[11px] text-error mt-1"><Trans i18nKey="announcements.pleaseSelectADepartment" /></p>}
              </div>
            }

            {/* Manager */}
            {recipient === "Reportees" &&
            <div>
                <label className={fieldLabel}><Trans i18nKey="common.manager" /> <span className="text-error">*</span></label>
                <Select
                value={managerId ? String(managerId) : undefined}
                onValueChange={(val) => {setManagerId(Number(val));clearFieldError("managerId");setSubmitError("");}}>
                
                  <SelectTrigger className={cn("h-10 rounded-xl bg-white", fieldErrors.managerId ? errBorder : "border-(--color-text-border)")}>
                    <SelectValue placeholder={i18n.t("announcements.selectManager")} />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.map((m) =>
                  <SelectItem key={m.managerId} value={String(m.managerId)}>{m.managerName}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
                {fieldErrors.managerId && <p className="text-[11px] text-error mt-1"><Trans i18nKey="announcements.pleaseSelectAManager" /></p>}
              </div>
            }

            {/* Content */}
            <div>
              <label className={fieldLabel}><Trans i18nKey="announcements.content" /> <span className="text-error">*</span></label>
              <Textarea
                className={cn("rounded-xl min-h-[110px]", fieldErrors.content ? errBorder : "border-(--color-text-border)")}
                placeholder={i18n.t("announcements.writeHere")}
                maxLength={5000}
                value={content}
                onChange={(e) => {setContent(e.target.value);clearFieldError("content");setSubmitError("");}} />
              
              <div className="flex justify-end text-[11px] text-muted-foreground mt-1">{content.length}/5000</div>
              {fieldErrors.content && <p className="text-[11px] text-error mt-1"><Trans i18nKey="validation.contentIsRequired" /></p>}
            </div>

            {/* Attachment */}
            <div>
              <label className={fieldLabel}><Trans i18nKey="announcements.attachmentOptionalMax1Document" /></label>

              {hasExistingDoc &&
              <div className="flex items-center justify-between gap-2 text-[13px] text-foreground bg-workspace/60 border border-(--color-text-border) rounded-lg px-3 py-2 mb-2">
                  <span className="truncate"><Trans i18nKey="announcements.supportingDocumentUploaded" /></span>
                  <span className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={handleViewExistingDoc} disabled={docDownloading}
                  className="text-[12px] font-semibold text-accent border border-(--color-text-border) rounded-md px-2.5 py-1 hover:bg-white disabled:opacity-60">
                      {docDownloading ? "Downloading…" : "Download"}
                    </button>
                    <button type="button" aria-label={i18n.t("formActions.removeUploadedDocument")}
                  onClick={() => {setExistingDocRemoved(true);setFileError("");}}
                  className="text-muted-foreground hover:text-error text-lg leading-none px-1">
                      &times;
                    </button>
                  </span>
                </div>
              }

              {attachedFiles.length > 0 &&
              <ul className="space-y-1 mb-2">
                  {attachedFiles.map((f) =>
                <li key={f.name} className="flex items-center justify-between gap-2 text-[13px] text-foreground bg-workspace/60 border border-(--color-text-border) rounded-lg px-3 py-2">
                      <span className="truncate">📄 {f.name}</span>
                      <button type="button" aria-label={`Remove ${f.name}`} onClick={removeFile}
                  className="text-muted-foreground hover:text-error text-lg leading-none px-1">&times;</button>
                    </li>
                )}
                </ul>
              }

              {!hasAnyDoc ?
              <label
                className="flex flex-col items-center justify-center gap-1 border border-dashed border-(--color-text-border) rounded-xl px-4 py-6 text-sm text-foreground bg-workspace/40 cursor-pointer text-center"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}>
                
                  <input type="file" accept=".pdf,.jpg,.jpeg,.doc,.docx" className="hidden" onChange={handleFileChange} />
                  <UploadCloud className="h-5 w-5 text-accent" />
                  <span><strong><Trans i18nKey="announcements.clickToUpload" /></strong> <Trans i18nKey="announcements.orDragAndDrop" /></span>
                  <span className="text-[11px] text-muted-foreground"><Trans i18nKey="announcements.pdfJpgDocsMax5mb" /></span>
                </label> :

              <p className="text-[11px] text-muted-foreground">
                  <Trans i18nKey="validation.onlyOneDocumentCanBeAttachedRemoveThe" />
                </p>
              }
              {fileError && <p className="text-[11px] text-error mt-1">{fileError}</p>}
            </div>
          </div>

          {submitError &&
          <div className="flex items-center gap-2 px-6 py-3 bg-error/10 border-t border-error/25 text-[13px] font-semibold text-error">
              <span aria-hidden="true">⚠</span> {submitError}
            </div>
          }

          <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-(--color-text-border)/70 shrink-0">
            <button type="button" onClick={() => onClose(false)}
            className="h-10 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold text-foreground hover:bg-workspace">
              <Trans i18nKey="formActions.cancel" />
            </button>
            {(!isEdit || isDraftResume) &&
            <button type="button" onClick={handleSaveDraft} disabled={submitting || savingDraft}
            className="h-10 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold text-accent hover:bg-workspace disabled:opacity-50">
                {savingDraft ? "Saving…" : "Save as Draft"}
              </button>
            }
            <button type="button" onClick={handleSubmit}
            disabled={submitting || savingDraft || !!archiveError || !!publishError}
            className="h-10 px-5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed">
              {submitLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
